import {
  TableRow
} from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import VoxelButton from '../../../../../../components/voxel/Buttons/VoxelButton';
import VoxelRadio from '../../../../../../components/voxel/Forms/VoxelRadio';
import VoxelTableCell from '../../../../../../components/voxel/Tables/VoxelTableCell';
import { TagGroupAutoColumnGq } from '../../../../../../graphql/projects/models/TagGroupAutoColumnGq';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';
import ConflictsRowCheckbox from './ConflictsRowCheckbox';
import { ConflictTableState } from './logic/useConflictTableState';
import { ConflictKeywordGq } from '../../../../../../graphql/projects/models/ConflictKeywordGq';

export interface ConflictsTableRowProps {
  projectId: number;
  tableState: ConflictTableState;
  column?: TagGroupAutoColumnGq;
  conflict: ConflictKeywordGq;
  reload: () => Promise<any>;
  index: number;
  onMouseDown?: (keywordId: number, index: number) => void;
  onMouseUp?: () => void;
  onMouseOver?: (keywordId: number, index: number) => void;
  dragSelected: boolean;
}

const ConflictsTableRow: React.FC<ConflictsTableRowProps> = ({
  conflict,
  tableState,
  column,
  reload,
  projectId,
  onMouseDown,
  onMouseOver,
  onMouseUp,
  index,
  dragSelected,
}) => {
  const [hide, setHide] = useState(false);
  const [selectedTag, setSelectedTag] = useState<number>(null);

  // Apply tag
  const [applying, setApplying] = useState(false);
  const applyTagMutation = projectsApiService.mutations.tags.useBulkTagKeywords();
  const handleApplyTag = () => {
    setApplying(true);
    setTimeout(() => setHide(true), 200);
    applyTagMutation({
      tagGroupId: conflict.tagGroupId,
      tagId: selectedTag,
      keywordQuery: {
        filtering: {
          keywordId: conflict.keywordId,
          projectId: projectId,
        },
      },
    }).catch(error => console.log(error))
      .then(reload)
      .finally(() => setApplying(false));
  };

  // Delete conflict
  const [deleting, setDeleting] = useState(false);
  const deleteConflictMutation = projectsApiService.mutations.tags.useDeleteConflicts();
  const handleDeleteConflicts = () => {
    setDeleting(true);
    setTimeout(() => setHide(true), 200);
    deleteConflictMutation({
      tagGroupId: conflict.tagGroupId,
      keywordQuery: {
        filtering: {
          keywordId: conflict.keywordId,
          projectId: projectId,
        },
      },
    }).then(reload)
      .finally(() => setDeleting(false));
  };

  const toggleTagSelection = (tagId: number) => {
    if (selectedTag === tagId) {
      setSelectedTag(null)
    } else {
      setSelectedTag(tagId)
    }
  };

  if (hide) {
    return null;
  }

  return (
    <TableRow
      key={conflict.keywordId}
      onMouseDown={onMouseDown ? () => onMouseDown(conflict.keywordId, index) : undefined}
      onMouseOver={onMouseOver ? () => onMouseOver(conflict.keywordId, index) : undefined}
      hover={!dragSelected}
      onMouseUp={onMouseUp}
      selected={dragSelected}
      className={`relative select-none cursor-pointer border-b border-voxel-gray-100`}
    >
      <VoxelTableCell size='sm'>
        <ConflictsRowCheckbox conflict={conflict} tableState={tableState} disabled={applying} />
      </VoxelTableCell>
      <VoxelTableCell size='lg'>
        {conflict.keyword}
      </VoxelTableCell >
      {column === TagGroupAutoColumnGq.TRANSLATION_ENGLISH && (
        <VoxelTableCell size='lg'>
          {conflict.translationEnglish}
        </VoxelTableCell>
      )}
      {column === TagGroupAutoColumnGq.TRANSLATED_FROM_KEYWORD && (
        <VoxelTableCell size='lg'>
          {conflict.translatedFromKeyword}
        </VoxelTableCell>
      )}
      <VoxelTableCell size='lg' >
        <span className='flex flex-row items-center gap-6'>
          {conflict.conflictTags.map((tag) => (
            <VoxelRadio
              key={tag.tagId}
              title={tag.tagName}
              value={tag.tagId}
              selectedOption={selectedTag}
              setSelectedOption={() => toggleTagSelection(tag.tagId)}
              onMouseDown={(event) => event.stopPropagation()}
              onMouseUp={(event) => event.stopPropagation()}
            />
          ))}
        </span>
      </VoxelTableCell>
      <VoxelTableCell size='md' align='right' >
        <span className='flex flex-row gap-2 justify-end '>
          <VoxelButton
            title={'Apply'}
            shape='square'
            size='md'
            disabled={applying || deleting || selectedTag === null}
            onClick={handleApplyTag}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
            color='gray'
          />
          <VoxelButton
            title={'Skip'}
            shape='square'
            size='md'
            disabled={deleting}
            onClick={handleDeleteConflicts}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
            color='light-gray'
          />
        </span>
      </VoxelTableCell>
    </TableRow>
  );
};

export default ConflictsTableRow;
